/**
 * The one switch the whole app uses.
 *
 * Drawn after the rounded-square switch the design asked for: a soft
 * rounded-rectangle track and a rounded-square knob that glides across. It
 * follows the theme - the track fills with the accent colour when on - and it
 * never rotates: the knob slides and the colour fades, over the same 400ms.
 *
 * A real checkbox sits underneath, so keyboards, screen readers and labels
 * all work the way they would with a native control.
 */

import { useId } from 'react';
import { cn } from '@/lib/cn';

export function Switch({ checked, onChange, disabled, label, labelledBy, describedBy, size = 'md', className }: {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  /** Needed when nothing visible names the switch. */
  label?: string;
  labelledBy?: string;
  describedBy?: string;
  size?: 'sm' | 'md';
  className?: string;
}) {
  const id = useId();
  return (
    <span
      className={cn(
        'relative inline-block flex-none',
        size === 'sm' ? 'h-[26px] w-[45px]' : 'h-[32px] w-[56px]',
        disabled && 'pointer-events-none opacity-45',
        className
      )}
    >
      <input
        id={id}
        type="checkbox"
        role="switch"
        checked={checked}
        disabled={disabled}
        aria-checked={checked}
        aria-label={labelledBy ? undefined : label}
        aria-labelledby={labelledBy}
        aria-describedby={describedBy}
        onChange={(e) => onChange(e.target.checked)}
        className="peer absolute inset-0 z-[1] m-0 h-full w-full cursor-pointer appearance-none rounded-[calc(10px*var(--radius-mul,1))] opacity-0"
      />
      {/* the track */}
      <span
        aria-hidden
        className={cn(
          'absolute inset-0 rounded-[calc(10px*var(--radius-mul,1))]',
          'transition-[background-color,box-shadow] duration-[400ms] ease-[var(--ease-out)] motion-reduce:transition-none',
          checked
            ? 'bg-accent shadow-[0_6px_16px_-8px_color-mix(in_srgb,var(--accent)_95%,transparent)]'
            : 'bg-[color-mix(in_srgb,var(--text-mute)_42%,var(--bg-3))]',
          'peer-focus-visible:shadow-[0_0_0_2px_var(--bg-1),0_0_0_4px_var(--accent)]'
        )}
      />
      {/* the knob: slides, never spins */}
      <span
        aria-hidden
        className={cn(
          'absolute bg-white shadow-[0_2px_5px_rgba(0,0,0,.25)]',
          'rounded-[calc(8px*var(--radius-mul,1))]',
          'transition-transform duration-[400ms] ease-[var(--ease-out)] motion-reduce:transition-none',
          size === 'sm' ? 'top-[4px] left-[4px] h-[18px] w-[18px]' : 'top-[5px] left-[5px] h-[22px] w-[22px]',
          checked ? (size === 'sm' ? 'translate-x-[19px]' : 'translate-x-[24px]') : 'translate-x-0'
        )}
      />
    </span>
  );
}
